import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { apiClient } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';
import WebSocketStatus from './WebSocketStatus';

interface HealthInfo {
    status: string;
    database_status: string;
    active_model?: string;
    rag_enabled?: boolean;
    docling_mock?: boolean;
    docling_serve?: string;
}

/**
 * 侧边栏状态指示器：徽标常显连接状态，点击弹出详细运行状态
 * （WebSocket 详情 + 后端健康信息），支持手动刷新。点击外部自动关闭。
 */
const StatusPopover: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [health, setHealth] = useState<HealthInfo | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const loadHealth = useCallback(async () => {
        setLoading(true);
        setError(null);
        const resp = await apiClient.get<HealthInfo>(API_ENDPOINTS.CONFIG.HEALTH);
        setLoading(false);
        if (resp.error) {
            setError(resp.error);
            setHealth(null);
        } else {
            setHealth(resp.data || null);
        }
    }, []);

    useEffect(() => {
        if (open) loadHealth();
    }, [open, loadHealth]);

    // 点击外部关闭
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const row = (label: string, value: React.ReactNode) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '3px 0' }}>
            <span style={{ color: '#666' }}>{label}</span>
            <span style={{ color: '#333', fontWeight: 500 }}>{value}</span>
        </div>
    );

    const boolBadge = (v: boolean | undefined, trueText: string, falseText: string) => (
        <span style={{ color: v ? '#389e0d' : '#999' }}>{v ? trueText : falseText}</span>
    );

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen(!open)}
                title="点击查看详细运行状态"
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
            >
                <WebSocketStatus showDetails={false} className="status-minimal" />
            </button>

            {open && (
                <div style={{
                    position: 'absolute', bottom: '36px', left: 0, width: '280px',
                    backgroundColor: '#fff', border: '1px solid #f0f0f0', borderRadius: '10px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: '12px 14px', zIndex: 1600,
                }}>
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        fontSize: '13px', fontWeight: 600, color: '#333',
                        paddingBottom: '6px', marginBottom: '8px', borderBottom: '1px solid #f0f0f0',
                    }}>
                        运行状态
                        <button
                            onClick={loadHealth}
                            title="刷新状态"
                            style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#1677ff', display: 'flex' }}
                        >
                            <RefreshCw size={13} className={loading ? 'ws-status-spin' : ''} />
                        </button>
                    </div>

                    <div style={{ marginBottom: '8px' }}>
                        <WebSocketStatus showDetails />
                    </div>

                    {error && <div style={{ fontSize: '12px', color: '#cf1322' }}>后端不可达：{error}</div>}
                    {health && (
                        <div style={{ backgroundColor: '#fafafa', borderRadius: '8px', padding: '8px 12px' }}>
                            {row('后端 API', boolBadge(health.status === 'healthy', '正常', '异常'))}
                            {row('数据库', boolBadge(health.database_status === 'healthy', '正常', '异常'))}
                            {row('当前模型', health.active_model || '-')}
                            {row('RAG 向量检索', boolBadge(health.rag_enabled, '开启', '关闭（原文直注）'))}
                            {row('文档解析 Mock', boolBadge(health.docling_mock, '开启（测试模式）', '关闭'))}
                            {row('Docling 服务', health.docling_serve || '-')}
                        </div>
                    )}

                    <style>{`
                        .ws-status-spin { animation: wsStatusSpin 1s linear infinite; }
                        @keyframes wsStatusSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                    `}</style>
                </div>
            )}
        </div>
    );
};

export default StatusPopover;
